import { useContext, useEffect, useMemo, useState } from "react";
import api, { CatalogAPI } from "../api";
import { UserContext } from "../context/UserContext";

export default function MyImpressum() {
  const { user } = useContext(UserContext);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    street: "",
    houseNumber: "",
    postalCode: "",
    city: "",
    country: "Deutschland",
    phone: "",
    email: "",
    vatId: "",
    smallBusiness: false,
    brandStart: '',
    brandMid: '',
    brandEnd: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [clientErrors, setClientErrors] = useState({});
  // Tiers/Subscriptions/Catalog state
  const [subs, setSubs] = useState([]);
  const [catalog, setCatalog] = useState({ services: [], packages: [] });
  const [copied, setCopied] = useState(false);
  const COUNTRIES = [
    'Deutschland','Österreich','Schweiz','Belgien','Luxemburg','Niederlande','Frankreich','Italien','Spanien','Portugal','Polen','Tschechien','Dänemark','Schweden','Norwegen'
  ];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [settingsRes, subsRes, catalogRes] = await Promise.all([
          api.get("/impressum/settings").catch(e => ({ data: null, _err: e })),
          api.get('/subscriptions/me').catch(() => ({ data: { items: [] } })),
          CatalogAPI.get().catch(() => ({ services: [], packages: [] })),
        ]);
        const data = settingsRes?.data || {};
        if (!mounted) return;
        if (data && Object.keys(data).length) {
          setForm({
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            company: data.company || "",
            street: data.street || "",
            houseNumber: data.houseNumber || "",
            postalCode: data.postalCode || "",
            city: data.city || "",
            country: data.country || "Deutschland",
            phone: data.phone || "",
            email: data.email || "",
            vatId: data.vatId || "",
            smallBusiness: !!data.smallBusiness,
            brandStart: data.brandStart || '',
            brandMid: data.brandMid || '',
            brandEnd: data.brandEnd || '',
          });
        }
        // subscriptions (to derive current tier)
        const items = subsRes?.data?.items || [];
        setSubs(items);
        // public catalog (to show prices)
        const cat = catalogRes?.services ? catalogRes : (catalogRes?.data || { services: [], packages: [] });
        setCatalog({ services: cat.services || [], packages: cat.packages || [] });
      } catch (e) {
        setError(e?.response?.data?.error || "Fehler beim Laden.");
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Tier helpers
  const normalizeTierFromName = (name) => {
    const n = String(name || '').toLowerCase();
    if (n.includes('extra')) return 'extra';
    if (n.includes('full')) return 'full';
    if (n.includes('lite')) return 'lite';
    if (n === 'impressum' || n === 'impressums-service' || n.includes('impressum') || n.includes('impressums-service')) return 'full';
    return null;
  };
  const myTier = useMemo(() => {
    // pick highest tier from assigned Impressum services
    let best = null; // lite < full < extra
    const rank = { lite: 1, full: 2, extra: 3 };
    for (const s of subs) {
      const nm = s?.Service?.name || s?.name;
      const t = normalizeTierFromName(nm);
      if (!t) continue;
      if (!best || rank[t] > rank[best]) best = t;
    }
    return best;
  }, [subs]);

  // Catalog lookup per tier
  const tierService = (tier) => {
    const names = tier === 'lite'
      ? ['Impressum Lite','Impressums-Service Lite']
      : tier === 'full'
      ? ['Impressum Full','Impressums-Service Full','Impressum']
      : ['Impressum Extra','Impressums-Service Extra'];
    const svc = (catalog.services || []).find(s => names.some(n => (s.name||'').toLowerCase() === n.toLowerCase()));
    return svc || null;
  };
  const VAT = 0.19;
  const fmt = (v) => Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const priceView = (svc) => {
    if (!svc) return <span>—</span>;
    const base = Number(svc.netPrice || 0);
    const pct = Number(svc.effectiveDiscountPct || 0);
    const hasDisc = pct > 0;
    const disc = hasDisc ? base * (1 - pct/100) : base;
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', gap:4 }}>
        {hasDisc && <span style={{ textDecoration:'line-through', color:'#999' }}>{fmt(base)}</span>}
        <span style={{ fontWeight:1000, color: hasDisc ? '#22c55e' : '#fff' }}>{fmt(disc)}</span>
        {hasDisc && <span style={{ fontSize:11, color:'#9FE870', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.35)', padding:'1px 6px', borderRadius:9999 }}>Aktion −{pct.toFixed(0)}%</span>}
        <span style={{ fontSize:12, color:'#bbb' }}>pro Monat (netto)</span>
      </div>
    );
  };

  const TIER_CONTENT = {
    lite: {
      key: 'lite',
      title: 'Impressums‑Service Lite',
      accent: '#3b82f6',
      features: [
        'Erfüllt die Impressumspflicht ohne Veröffentlichung deiner privaten Adresse',
        'Ladungsfähige postalische Adresse (rechtskonform)',
        'Eingehende Briefe werden per Post weitergeleitet; auf Wunsch zusätzlich digital per E‑Mail',
        'Päckchen & Pakete werden nicht angenommen',
      ],
    },
    full: {
      key: 'full',
      title: 'Impressums‑Service Full',
      accent: '#22c55e',
      features: [
        'Alles aus Lite enthalten',
        'Eigene Impressumsseite (Hosting inklusive Updates)',
        'Paketannahme inklusive',
        'Pakete werden ungeöffnet an dich weitergeleitet',
        'Entstehende Portokosten werden in Rechnung gestellt',
      ],
    },
    extra: {
      key: 'extra',
      title: 'Impressums‑Service Extra',
      accent: '#f59e0b',
      features: [
        'Alles aus Full enthalten',
        'Pakete werden vor Weiterleitung geprüft',
        'Unerwünschte Inhalte werden entfernt und entsorgt',
        'Entstehende Portokosten werden in Rechnung gestellt',
      ],
    }
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'smallBusiness' && !checked && !form.vatId) {
      // leaving both empty would be invalid; but we just clear error on submit
    }
    if (name === 'postalCode') {
      // simple numeric only, max 10 chars
      const v = value.replace(/[^0-9]/g, '').slice(0, 10);
      setForm(prev => ({ ...prev, postalCode: v }));
    }
    if (name === 'phone') {
      // allow +, numbers, spaces, hyphens; trim length
      const v = value.replace(/[^0-9+\-\s]/g, '').slice(0, 32);
      setForm(prev => ({ ...prev, phone: v }));
    }
    // clear field-level error when editing
    setClientErrors(prev => ({ ...prev, [name]: '' }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaved(false);
    setError("");
    // client validation
    const errs = {};
  const reqFields = ['firstName','lastName','street','houseNumber','postalCode','city','country','phone','email'];
    reqFields.forEach(k => { if (!String(form[k]||'').trim()) errs[k] = 'Pflichtfeld'; });
    if (!form.smallBusiness && !String(form.vatId||'').trim()) errs['vatId'] = 'USt-IdNr. oder Kleinunternehmer wählen';
    if (form.postalCode && !/^\d{3,10}$/.test(form.postalCode)) errs['postalCode'] = 'Ungültige PLZ';
    if (form.phone && !/^[0-9+\-\s]{5,32}$/.test(form.phone)) errs['phone'] = 'Ungültige Telefonnummer';
  if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs['email'] = 'Ungültige E-Mail-Adresse';
    setClientErrors(errs);
    if (Object.keys(errs).length > 0) {
      return; // don’t submit
    }
    try {
    const payload = { ...form };
      // empty strings to null for optional
      if (!payload.company) payload.company = null;
      if (!payload.vatId) payload.vatId = null;
  // normalize empty color strings to null
  if (!payload.brandStart) payload.brandStart = null;
  if (!payload.brandMid) payload.brandMid = null;
  if (!payload.brandEnd) payload.brandEnd = null;
      const { data } = await api.put("/impressum/settings", payload);
      setSaved(true);
    } catch (e) {
      setError(e?.response?.data?.error || "Speichern fehlgeschlagen.");
    }
  };

  return (
    <div className="home-page">
      {/* Hero/Intro */}
      <section style={{ width: '100vw', margin: 0, padding: 0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Impressums‑Service</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Verwalte deine Impressumsangaben für deine Seiten und Shops.</p>
      </section>
      <section style={{ width: '100%', maxWidth: 920, margin: '0 auto', padding: '0 14px', color: '#fff' }}>
        {/* Info block */}
        <div
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            padding: '16px 16px',
            marginBottom: 16,
            lineHeight: 1.6
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Rechtssicher mit dem AP‑N Impressums‑Service</div>
          <p style={{ marginTop: 6, opacity: .9 }}>
            Gesetzlich vorgeschrieben ist, dass im Impressum eine voll­ständige namentliche Angabe erscheint.
            Unser Service stellt dir dafür eine ladungsfähige Geschäftsadresse bereit – so musst du deine private Wohnanschrift nicht veröffentlichen.
          </p>
          <p style={{ marginTop: 6, opacity: .9 }}>
            In Deutschland und der EU gelten klare Pflichten für öffentliche Personen und gewerbliche Content‑Creator:
            Eine ladungsfähige Anschrift ist zwingend anzugeben, um §5 DDG (Digitale‑Dienste‑Gesetz) und §18 MStV (Medienstaatsvertrag) zu erfüllen.
            Ohne korrektes Impressum drohen Abmahnungen, Bußgelder oder weitere rechtliche Schritte.
          </p>
          <p style={{ marginTop: 6, opacity: .9 }}>
            Das Impressum ist rechtlich öffentlich einsehbar. Über unseren Service nutzt du jedoch eine bereitgestellte, ladungsfähige Geschäftsadresse statt deiner Privatanschrift.
            Nach Vertragsende musst du das Impressum unverzüglich entfernen. Bei fortgesetzter Nutzung können Vertragsstrafen entstehen.
            Bei anhaltender Missachtung kann es an ein Inkasso übergeben oder gerichtlich verfolgt werden.
          </p>
          <p style={{ marginTop: 6, opacity: .9 }}>
            Da Name und ladungsfähige Anschrift öffentlich sind, können sie grundsätzlich von jeder Person eingesehen werden.
            Weitere persönliche Informationen (z. B. private Wohnadresse, E‑Mail, Telefon) geben wir ausschließlich an Behörden, 
            Rechtsvertretungen oder Personen mit nachweislich berechtigtem Interesse heraus.
            Du kannst die hinterlegten Angaben jederzeit hier aktualisieren; Änderungen werden sofort übernommen.
          </p>
          <div style={{
            marginTop: 10,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 10
          }}>
            <div style={{ background: 'rgba(46,204,113,0.12)', border: '1px solid rgba(46,204,113,0.3)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700 }}>Vorteile</div>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                <li>Rechtssicherheit nach §5 DDG & §18 MStV</li>
                <li>Schutz deiner privaten Wohnanschrift</li>
                <li>Weniger Risiko von Stalking & Belästigung</li>
                <li>Optimal für Streamer, YouTuber & Creator</li>
                <li>Transparente Prozesse und kurze Reaktionszeiten</li>
              </ul>
            </div>
            <div style={{ background: 'rgba(52,152,219,0.12)', border: '1px solid rgba(52,152,219,0.3)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700 }}>Unser Service</div>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                <li>Ladungsfähige Geschäftsadresse</li>
                <li>Versand von Unterlassungsaufforderungen</li>
                <li>Juristische Begleitung bei Bedarf</li>
                <li>Offizielle Vertretung in deinem Namen</li>
              </ul>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.02) 100%)',
          border: '1px solid #222',
          borderRadius: 14,
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          padding: 22,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 26 }}>📄</div>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Mein Impressum</h2>
              <div style={{ opacity: .75, fontSize: 13 }}>Pflichtfelder mit * · Entweder USt‑IdNr. oder Kleinunternehmerregelung</div>
            </div>
          </div>

          {/* Status messages */}
          {error && !saved ? (
            <div style={{
              color: '#fff', background: '#e74c3c', marginBottom: 16, fontWeight: 600, borderRadius: 8, padding: '10px 14px',
              boxShadow: '0 2px 12px rgba(231, 76, 60, 0.35)'
            }}>{error}</div>
          ) : null}
          {saved ? (
            <div style={{
              color: '#0f0', background: 'rgba(46, 204, 113, 0.12)', marginBottom: 16, fontWeight: 600, border: '1px solid rgba(46, 204, 113, 0.35)',
              borderRadius: 8, padding: '10px 14px', boxShadow: '0 2px 12px rgba(46, 204, 113, 0.15)'
            }}>Gespeichert.</div>
          ) : null}

          {/* Loading skeleton */}
          {loading ? (
            <div style={{ opacity: .6, padding: 16 }}>Lade…</div>
          ) : (
            <form onSubmit={onSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <LabeledInput label="Vorname *" name="firstName" value={form.firstName} onChange={onChange} required error={clientErrors.firstName} />
              <LabeledInput label="Nachname *" name="lastName" value={form.lastName} onChange={onChange} required error={clientErrors.lastName} />

              <LabeledInput label="Firmenname (falls vorhanden)" name="company" value={form.company} onChange={onChange} full />

              <div style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
                <LabeledInput label="Straße *" name="street" value={form.street} onChange={onChange} required error={clientErrors.street} />
                <LabeledInput label="Hausnummer *" name="houseNumber" value={form.houseNumber} onChange={onChange} required error={clientErrors.houseNumber} />
              </div>

              <LabeledInput label="PLZ *" name="postalCode" value={form.postalCode} onChange={onChange} required error={clientErrors.postalCode} />
              <LabeledInput label="Ort *" name="city" value={form.city} onChange={onChange} required error={clientErrors.city} />
              <LabeledSelect label="Land *" name="country" value={form.country} onChange={onChange} options={COUNTRIES} required error={clientErrors.country} />
              <LabeledInput label="Telefon * (Mobil bevorzugt)" name="phone" value={form.phone} onChange={onChange} required error={clientErrors.phone} />
              <LabeledInput label="E‑Mail *" name="email" value={form.email} onChange={onChange} required error={clientErrors.email} />

              <div style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'center' }}>
                <LabeledInput label="USt-IdNr." name="vatId" value={form.vatId} onChange={onChange} placeholder="DE123456789" error={clientErrors.vatId} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22 }}>
                  <input id="smallBusiness" type="checkbox" name="smallBusiness" checked={form.smallBusiness} onChange={onChange} />
                  <label htmlFor="smallBusiness">Kleinunternehmerregelung (§19 UStG) anwenden</label>
                </div>
                <span style={{ opacity: .75, fontSize: 13 }}>Hinweis: Entweder USt‑IdNr. angeben oder die Kleinunternehmerregelung aktivieren.</span>
              </div>

              {/* Public page background colors */}
              <div style={{ gridColumn: 'span 2', marginTop: 4, paddingTop: 8, borderTop: '1px dashed #333' }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Hintergrund‑Farbverlauf (öffentliche Seite)</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap: 12, alignItems: 'end' }}>
                  <ColorInput label="Farbe oben" name="brandStart" value={form.brandStart} onChange={onChange} placeholder="#0b1f1a" />
                  <ColorInput label="Farbe Mitte" name="brandMid" value={form.brandMid} onChange={onChange} placeholder="#0d2b22" />
                  <ColorInput label="Farbe unten" name="brandEnd" value={form.brandEnd} onChange={onChange} placeholder="#0a1210" />
                </div>
                <div style={{ fontSize: 12, opacity: .7, marginTop: 6 }}>Leer lassen für Standardfarben. Unterstützt #Hex, rgb()/rgba(), hsl()/hsla().</div>
              </div>
              
              {/* Inline live preview */}
              <div style={{ gridColumn: 'span 2' }}>
                <ImpressumPreview data={form} colors={{ start: form.brandStart, mid: form.brandMid, end: form.brandEnd }} />
              </div>
              <div style={{ gridColumn: 'span 2', marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', justifyContent:'space-between' }}>
                <div>
                  <button type="submit" className="hero-btn-primary" style={{ padding: '10px 16px', borderRadius: 8, fontWeight: 700 }}>Speichern</button>
                </div>
                {(myTier === 'full' || myTier === 'extra') && user?.id && (
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    {copied && <span style={{ fontSize:12, color:'#9FE870' }}>URL kopiert.</span>}
                    <button type="button" onClick={async () => {
                      try {
                        const base = (api?.defaults?.baseURL || '').replace(/\/$/, '');
                        const url = `${base}/impressum/page/${user.id}`;
                        await navigator.clipboard.writeText(url);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      } catch {}
                    }}
                      style={{ padding:'10px 12px', borderRadius:8, border:'1px solid #333', background:'#0b0b0b', color:'#fff', fontWeight:700 }}
                    >URL kopieren</button>
                    <button type="button" onClick={() => {
                      const base = (api?.defaults?.baseURL || '').replace(/\/$/, '');
                      const url = `${base}/impressum/page/${user.id}`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                      className="hero-btn-primary"
                      style={{ padding:'10px 12px', borderRadius:8, fontWeight:700 }}
                    >Seite öffnen</button>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>
      </section>
      {/* Tiers & Upgrade */}
      <section style={{ width: '100%', maxWidth: 1100, margin: '16px auto 40px', padding: '0 16px', color: '#fff' }}>
        <h3 style={{ margin: '10px 0 12px', fontSize: 20, fontWeight: 800 }}>Impressum‑Pakete</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          {(['lite','full','extra']).map(tier => {
            const conf = TIER_CONTENT[tier];
            const svc = tierService(tier);
            const isCurrent = myTier === tier;
            const isHigherThanCurrent = myTier && ((myTier === 'lite' && (tier==='full'||tier==='extra')) || (myTier==='full' && tier==='extra'));
            return (
              <div key={tier} style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.02) 100%)',
                border: `1px solid ${isCurrent ? conf.accent : '#222'}`,
                borderRadius: 14,
                boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                padding: 16,
                position:'relative'
              }}>
                {isCurrent && (
                  <div style={{ position:'absolute', top:10, right:10, fontSize:12, fontWeight:800, color:'#0f172a', background: conf.accent, padding:'4px 8px', borderRadius:9999 }}>
                    Gebucht
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:10 }}>
                  <div style={{ fontSize:18, fontWeight:800 }}>{conf.title}</div>
                </div>
                <div style={{ marginBottom:10 }}>
                  {priceView(svc)}
                </div>
                <ul style={{ margin:0, padding:'0 0 0 18px', color:'#ddd', lineHeight:1.6 }}>
                  {conf.features.map((f, i) => (<li key={i}>{f}</li>))}
                </ul>
                <div style={{ marginTop:14, display:'flex', gap:8 }}>
                  {isCurrent ? (
                    <button disabled style={{ opacity:.8, cursor:'default', padding:'8px 12px', borderRadius:8, border:'1px solid #333', background:'#0b0b0b', color:'#bbb' }}>Aktiv</button>
                  ) : (!myTier || isHigherThanCurrent) ? (
                    <a href="/angebote" style={{ textDecoration:'none' }}>
                      <button className="hero-btn-primary" style={{ padding:'8px 12px', borderRadius:8, fontWeight:800 }}>Jetzt upgrade sichern</button>
                    </a>
                  ) : (
                    <button disabled style={{ opacity:.8, cursor:'not-allowed', padding:'8px 12px', borderRadius:8, border:'1px solid #333', background:'#0b0b0b', color:'#bbb' }}>Niedriger als aktuell</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {myTier !== 'extra' && (
          <div style={{ marginTop:16, background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.35)', borderRadius:10, padding:12 }}>
            <b>Mehr Leistung gefällig?</b> Upgrade auf <span style={{ fontWeight:800 }}>Impressum Extra</span> für maximale Absicherung und Priorisierung. 
            <a href="/angebote" style={{ marginLeft:10, fontWeight:800, color:'#9FE870' }}>Angebote ansehen →</a>
          </div>
        )}
        <div style={{ marginTop:12, background:'rgba(59,130,246,0.12)', border:'1px solid rgba(59,130,246,0.35)', borderRadius:10, padding:12, color:'#dbeafe' }}>
          <b>Tipp:</b> Bei einigen <i>Standard‑Paketen</i> und/oder einem <i>individuellen Dienstleistungspaket</i> können Portokosten bereits mit abgedeckt sein. 
          <a href="/angebote" style={{ marginLeft:6, fontWeight:800, color:'#93c5fd' }}>Zu den Paketen →</a>
        </div>
      </section>
    </div>
  );
}

function LabeledInput({ label, name, value, onChange, required, placeholder, full }) {
  return (
    <div style={{ gridColumn: full ? 'span 2' : 'span 1' }}>
      <label style={{ display: 'block', fontSize: 13, opacity: .85, marginBottom: 6 }}>{label}</label>
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 8,
          border: '1px solid #333', background: '#111', color: '#fff',
          outline: 'none', boxShadow: '0 1px 8px rgba(0,0,0,0.25)'
        }}
      />
      {arguments[0]?.error ? <div style={{ color: '#e74c3c', fontSize: 12, marginTop: 6 }}>{arguments[0].error}</div> : null}
    </div>
  );
}

function ColorInput({ label, name, value, onChange, placeholder }) {
  const id = `color-${name}`;
  const setValue = (v) => onChange({ target: { name, value: v, type: 'text' } });
  const onPick = (e) => setValue(e.target.value);
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: 13, opacity: .85, marginBottom: 6 }}>{label}</label>
      <div style={{ display:'flex', gap:8 }}>
        <input
          id={id}
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{ flex:1, padding:'10px 12px', borderRadius:8, border:'1px solid #333', background:'#111', color:'#fff', outline:'none', boxShadow:'0 1px 8px rgba(0,0,0,0.25)' }}
        />
        <input type="color" value={/^#([0-9a-fA-F]{6})$/.test(value||'') ? value : '#0b1f1a'} onChange={onPick} title="Farbe wählen" style={{ width:44, height:44, padding:0, border:'1px solid #333', borderRadius:8, background:'#111' }} />
      </div>
    </div>
  );
}

function LabeledSelect({ label, name, value, onChange, options, required, error }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, opacity: .85, marginBottom: 6 }}>{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 8,
          border: '1px solid #333', background: '#111', color: '#fff',
          outline: 'none', boxShadow: '0 1px 8px rgba(0,0,0,0.25)'
        }}
      >
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
      {error ? <div style={{ color: '#e74c3c', fontSize: 12, marginTop: 6 }}>{error}</div> : null}
    </div>
  );
}

// Live preview of Impressum block
function ImpressumPreview({ data, colors }) {
  const nameLine = [data.firstName, data.lastName].filter(Boolean).join(' ');
  const lines = [];
  if (data.company) lines.push(data.company);
  if (nameLine) lines.push(nameLine);
  lines.push('AP-N Impressums-Service');
  lines.push('c/o AP-Network UG (haftungsbeschränkt)');
  lines.push('Wittenberger Weg 17');
  lines.push('24941 Flensburg');
  lines.push('Germany');
  const start = colors?.start || '#3054bd';
  const mid = colors?.mid || '#111e3d';
  const end = colors?.end || '#6731b2';
  return (
    <div style={{ marginTop: 16, padding: 14, borderRadius: 10, border: '1px solid #333', background: `radial-gradient(500px 300px at 8% -20%, rgba(50,205,137,0.18), transparent 60%), linear-gradient(180deg, ${start}, ${mid} 35%, ${end})` }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Vorschau</div>
      <div style={{ background:'#0f1115', border:'1px solid #333', borderRadius:8, padding:12 }}>
        <pre style={{ margin:0, whiteSpace:'pre-wrap', lineHeight:1.6, fontFamily:'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', color:'#d1d5db', background:'transparent' }}>
{lines.join('\n')}
        </pre>
      </div>
    </div>
  );
}
